import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Select, Input, DatePicker, Tooltip, Skeleton, Empty } from "antd";
import Pagination from "@mui/material/Pagination";
import {
  FileTextOutlined, PlusOutlined, ReloadOutlined, SearchOutlined,
  EyeOutlined, CheckOutlined, CloseOutlined, SwapOutlined, LoadingOutlined,
  LinkOutlined, CopyOutlined, WalletOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { API_BASE_URL } from "../../config";
import { useProposalSends, useUpdateSendStatus } from "../hooks/useProposalSends";
import { fetchSend } from "../services/proposalSendService";
import OfflinePaymentModal from "./OfflinePaymentModal";
import { toast } from "react-toastify";
import { authHeaders } from "../utils/session";

/**
 * Proposal & Quotation — the send log.
 *
 * This is the landing screen: what went out, to whom, through which channel, and
 * where it stands. Creating a document is a step away from here rather than the
 * first thing the module does, because most visits are to check on something
 * already sent.
 */

const PAGE_SIZE = 20;

const STATUSES = ["sent", "viewed", "accepted", "rejected", "expired"];
/* Only the ways a document can actually leave this screen. A row logged
   under a retired channel still renders — the badge shows whatever came
   back — it just cannot be filtered for. */
const CHANNELS = ["email", "whatsapp", "download"];

/* Status is the column people scan, so each one gets its own colour rather than
   a single neutral chip. */
const STATUS_STYLE = {
  sent: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  /* Downloading produces the document; it does not deliver it. Muted rather
     than blue, because nothing has reached the client yet. */
  downloaded: { bg: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
  viewed: { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
  accepted: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  rejected: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
  expired: { bg: "#f5f5f5", color: "#6b7280", border: "#e5e7eb" },
};

/* Payment is a second, independent state: a quotation can be accepted and
   still unpaid, so it gets its own badge rather than being folded into the
   document status. */
const PAYMENT_STYLE = {
  paid: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0", label: "Paid" },
  unpaid: { bg: "#fffbeb", color: "#b45309", border: "#fde68a", label: "Payment pending" },
  /* Part paid is its own state, not a kind of unpaid — money has come in and the
     balance is what is being chased. */
  partial: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", label: "Part paid" },
  pending_verification: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe", label: "Awaiting verification" },
  failed: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca", label: "Payment failed" },
};

const badge = (style) => ({
  display: "inline-block", background: style.bg, color: style.color,
  border: `1px solid ${style.border}`, borderRadius: 20, padding: "2px 11px",
  fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
});

const card = {
  background: "white", borderRadius: 16, border: "1px solid #f0f0f0",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden",
};
const th = {
  padding: "13px 16px", textAlign: "left", fontSize: 11.5, fontWeight: 700, color: "#475569",
  textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap",
};
const td = { padding: "13px 16px", fontSize: 13, color: "#374151", verticalAlign: "middle" };
const fieldLabel = { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };

const fmtDateTime = (d) =>
  d ? dayjs(d).format("DD MMM YYYY, hh:mm A") : "—";

/* The API sends this one in UTC with no zone on it, so it has to be read as UTC
   and not as whatever the browser happens to be in — otherwise a link that dies
   at midnight UTC reads as dying at 5:30 am to a rep in India. */
dayjs.extend(utc);
const fmtLinkExpiry = (d) => dayjs.utc(d).local().format("DD MMM, hh:mm A");

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "—");
const capitaliseOrBlank = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

/** Joins the parts that exist, so a missing one leaves no stray separator. */
const joinDot = (parts) => parts.filter(Boolean).join(" · ") || "—";

/* Days, or a dash when the send did not carry the setting. Both live in one
   column: they are a pair of internal timings, not two things anyone scans
   separately, and the table already carries ten columns. */
const days = (n) => (n == null || n === "" ? "—" : `${Number(n)}d`);

/* Same rule as the documents: what was charged, not a rounded version of it. */
const inr = (n) => {
  if (n == null || n === "") return "—";
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  const paise = Math.abs(value % 1) > 0;
  return `₹${value.toLocaleString("en-IN", {
    minimumFractionDigits: paise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

/* Nothing left to record once the client has answered — or the offer lapsed. */
const CLOSED = new Set(["accepted", "rejected", "expired"]);

const createPath = (docType) =>
  docType === "quotation" ? "/cms/quotations/create" : "/cms/proposals/create";

/** What the other document is called, for the "quote them the other one" action. */
const OTHER = { proposal: "quotation", quotation: "proposal" };

const actionBtn = (color = "#5b48e8", bg = "#f5f3ff", border = "#ddd6fe") => ({
  width: 30, height: 30, borderRadius: 8, border: `1px solid ${border}`, background: bg,
  color, cursor: "pointer", display: "inline-flex", alignItems: "center",
  justifyContent: "center", fontSize: 12, textDecoration: "none",
});

/**
 * The two screens this log serves.
 *
 * Proposals and quotations are worked on by different people at different points
 * in a deal, so they get a screen each rather than a shared list with a filter
 * on top. Nothing else differs — the same rows, actions and pricing.
 *
 * A renewal is a quotation the system raised on its own for the next term, so it
 * belongs beside the ones a rep sent by hand. It keeps its own badge and can be
 * filtered out, but it is never hidden: a renewal sitting unpaid is exactly the
 * thing someone opens this screen to find.
 */
const SCOPES = {
  proposal: {
    path: "/cms/proposals",
    types: ["proposal"],
    title: "Manage Proposal",
    subtitle: "What has gone out, to whom, and where it stands",
    createTo: "/cms/proposals/create",
    createLabel: "Create Proposal",
    cards: [{ key: "proposal", label: "Proposals", color: "#2563eb", bg: "#eff6ff" }],
  },
  quotation: {
    path: "/cms/quotations",
    types: ["quotation", "renewal"],
    title: "Manage Quotation",
    subtitle: "Quotations and renewals — what is owed, and what has been paid",
    createTo: "/cms/quotations/create",
    createLabel: "Create Quotation",
    cards: [
      { key: "quotation", label: "Quotations", color: "#0d9488", bg: "#f0fdfa" },
      { key: "renewal", label: "Renewals", color: "#7c3aed", bg: "#f5f3ff" },
    ],
  },
};

const TYPE_STYLE = {
  proposal: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  quotation: { bg: "#f0fdfa", color: "#0d9488", border: "#99f6e4" },
  renewal: { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
};

const ProposalLogs = ({ scope = "proposal" }) => {
  const view = SCOPES[scope] || SCOPES.proposal;
  /* Only meaningful where a screen holds more than one kind of document. */
  const multiType = view.types.length > 1;

  const [docType, setDocType] = useState(null);
  const [status, setStatus] = useState(null);
  const [channel, setChannel] = useState(null);
  const [sentBy, setSentBy] = useState(null);
  const [range, setRange] = useState([null, null]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // A changed filter means a different result set; page 1 is the only safe place.
  useEffect(() => {
    setPage(1);
  }, [docType, status, channel, sentBy, range, debouncedSearch]);

  /* The "sent by" picker is the same scoped list the rest of the CMS uses, so a
     manager only sees their own people in it. */
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/get-internal-user-list`, {
        headers: { ...authHeaders() },
        params: { all: 1 },
      })
      .then((res) => setMembers(res.data?.data || []))
      .catch(() => setMembers([]));
  }, []);

  /* A screen never shows what belongs to the other one, so the scope's own types
     are always sent — narrowed to one when the rep picks from the Type filter.
     Joined with a comma because a quotation screen carries two. */
  const filters = useMemo(
    () => ({
      doc_type: docType || view.types.join(","),
      ...(status ? { status } : {}),
      ...(channel ? { channel } : {}),
      ...(sentBy ? { sent_by: sentBy } : {}),
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      ...(range[0] ? { from_date: range[0].format("YYYY-MM-DD") } : {}),
      ...(range[1] ? { to_date: range[1].format("YYYY-MM-DD") } : {}),
      page,
      limit: PAGE_SIZE,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [docType, status, channel, sentBy, debouncedSearch, range, page, scope],
  );

  const { data, isLoading, isFetching, refetch } = useProposalSends(filters);
  const updateStatus = useUpdateSendStatus();
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  // Which row is being fetched, so only that row's buttons show the wait.
  const [opening, setOpening] = useState(null);
  const [hovered, setHovered] = useState(null);
  /* Set when a quotation's "from proposal" tag is followed, so the row it points
     at is findable in a long list rather than merely scrolled to. */
  const [highlighted, setHighlighted] = useState(null);
  /* The quotation whose offline payment is being recorded, if any. Opened right
     after a quotation is accepted — that is the moment the rep knows how the
     client is paying — and reachable afterwards from the row itself. */
  const [payingFor, setPayingFor] = useState(null);

  /* Already accepted rows come through here from the wallet button, where
     re-sending the status would be a write with nothing behind it. */
  const markAccepted = (row) => {
    if ((row.effective_status || row.status) === "accepted") return;
    updateStatus.mutate({ id: row.id, status: "accepted" });
  };

  /**
   * A proposal closes on the click. A quotation does not — accepting it raises
   * the question of how the money is coming, and the answer is what decides
   * whether it is really accepted. So the form opens first and the status
   * follows: either the payment is recorded, or the rep says the client is
   * paying online. Closing the form without answering leaves the row as it was,
   * which is the honest outcome of a click someone changed their mind about.
   */
  const accept = (row) => {
    // A renewal is billed like a quotation, so it asks the same question.
    if (row.doc_type !== "proposal") { setPayingFor(row); return; }
    markAccepted(row);
  };

  const jumpTo = (id) => {
    const el = document.getElementById(`send-row-${id}`);
    if (!el) return false;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlighted(id);
    setTimeout(() => setHighlighted((cur) => (cur === id ? null : cur)), 2400);
    return true;
  };

  /**
   * Follow a link between a proposal and the quotation it produced.
   *
   * The two now live on separate screens, so the row being pointed at is usually
   * not one this page can scroll to. Scrolling is tried first — a renewal can
   * point at a quotation sitting right above it — and otherwise the other screen
   * is opened and told which row to find, so the link lands somewhere either way
   * instead of dead-ending in a toast.
   */
  const openLinked = (id, targetScope) => {
    if (jumpTo(id)) return;
    if (targetScope === scope) {
      return toast.info("That row is not on this page — clear the filters to find it.");
    }
    navigate(SCOPES[targetScope].path, { state: { highlight: id } });
  };

  /* Arriving from the other screen: find the row that was asked for, once the
     rows are actually on screen to be scrolled to. */
  useEffect(() => {
    const wanted = navState?.highlight;
    if (!wanted || isLoading) return;
    const found = jumpTo(wanted);
    if (!found) toast.info(`#${wanted} is not on this page — search for the client to find it.`);
    // Consumed, so a later re-render does not scroll the page again.
    navigate(".", { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navState?.highlight, isLoading]);

  const copyLink = (link) => {
    navigator.clipboard.writeText(link)
      .then(() => toast.success("Payment link copied"))
      .catch(() => toast.error("Could not copy — the browser blocked it"));
  };

  /**
   * Reopen a send's inputs on a create screen.
   *
   * The list rows do not carry `payload`, so it is fetched first — which is also
   * what makes "send them the quotation too" a single click instead of typing
   * the whole form again.
   */
  const reopen = async (row, docType) => {
    setOpening(`${row.id}:${docType}`);
    try {
      const full = await fetchSend(row.id);
      if (!full?.payload) throw new Error("This send did not record its inputs");
      navigate(createPath(docType), { state: { payload: full.payload } });
    } catch (err) {
      toast.error(err?.message || "That send could not be opened");
    } finally {
      setOpening(null);
    }
  };

  const rows = data?.rows || [];
  const counts = data?.counts || { proposal: 0, quotation: 0, total: 0 };
  const pageCount = data?.pagination?.total_pages || 1;
  const total = data?.pagination?.total ?? counts.total;

  const createBtn = {
    display: "inline-flex", alignItems: "center", gap: 7, height: 40, padding: "0 18px",
    borderRadius: 10, fontWeight: 700, fontSize: 13.5, textDecoration: "none",
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#5b48e8 0%,#7c3aed 100%)", borderRadius: 16, padding: "26px 30px", marginBottom: 20, boxShadow: "0 8px 32px rgba(91,72,232,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 54, height: 54, background: "rgba(255,255,255,.22)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "white" }}>
                <FileTextOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 23, fontWeight: 700 }}>{view.title}</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: 14 }}>
                  {view.subtitle}
                </p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Tooltip title="Refresh">
                <button
                  onClick={() => refetch()}
                  style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}
                >
                  <ReloadOutlined spin={isFetching} />
                </button>
              </Tooltip>
              {/* One button, for the document this screen is about. The other
                  one lives on its own screen now. */}
              <Link
                to={view.createTo}
                style={{ ...createBtn, border: "none", background: "white", color: "#5b48e8" }}
              >
                <PlusOutlined /> {view.createLabel}
              </Link>
            </div>
          </div>

          {/* Counts — they follow the filters, so they never disagree with the table. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 20 }}>
            {[
              { label: "Total sent", value: counts.total, color: "#5b48e8", bg: "#f5f3ff" },
              // Only the kinds this screen actually holds.
              ...view.cards.map((c) => ({ ...c, value: counts[c.key] })),
            ].map((c) => (
              <div key={c.label} style={{ ...card, padding: "18px 20px", display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: c.bg, color: c.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                  <FileTextOutlined />
                </div>
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px" }}>{c.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>{c.value ?? 0}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div style={{ ...card, marginBottom: 20 }}>
            <div style={{ padding: "16px 20px", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              {/* Dropped where a screen holds one kind of document — a filter
                  whose only option is already applied is just a dead control. */}
              {multiType && (
                <div style={{ minWidth: 150 }}>
                  <label style={fieldLabel}>Type</label>
                  <Select
                    value={docType} onChange={setDocType} allowClear placeholder="All"
                    style={{ width: "100%" }}
                    options={view.types.map((x) => ({ value: x, label: capitalise(x) }))}
                  />
                </div>
              )}
              <div style={{ minWidth: 150 }}>
                <label style={fieldLabel}>Status</label>
                <Select
                  value={status} onChange={setStatus} allowClear placeholder="All"
                  style={{ width: "100%" }}
                  options={STATUSES.map((x) => ({ value: x, label: capitalise(x) }))}
                />
              </div>
              <div style={{ minWidth: 150 }}>
                <label style={fieldLabel}>Channel</label>
                <Select
                  value={channel} onChange={setChannel} allowClear placeholder="All"
                  style={{ width: "100%" }}
                  options={CHANNELS.map((x) => ({ value: x, label: capitalise(x) }))}
                />
              </div>
              <div style={{ minWidth: 190 }}>
                <label style={fieldLabel}>Sent by</label>
                <Select
                  value={sentBy} onChange={setSentBy} allowClear placeholder="Anyone"
                  style={{ width: "100%" }} showSearch optionFilterProp="label"
                  options={members.map((m) => ({ value: m.id, label: m.name }))}
                />
              </div>
              <div style={{ minWidth: 230 }}>
                <label style={fieldLabel}>Date range</label>
                <DatePicker.RangePicker
                  value={range} onChange={(v) => setRange(v || [null, null])}
                  format="DD-MM-YYYY" style={{ width: "100%" }}
                />
              </div>
              <div style={{ minWidth: 220, flex: 1 }}>
                <label style={fieldLabel}>Search</label>
                <Input
                  value={search} onChange={(e) => setSearch(e.target.value)} allowClear
                  placeholder="Email, phone, client…"
                  prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div style={card}>
            {isLoading ? (
              <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 6 }} /></div>
            ) : rows.length === 0 ? (
              <div style={{ padding: "48px 20px" }}>
                <Empty description="Nothing has been sent that matches these filters." />
              </div>
            ) : (
              <>
                <div style={{ overflowX: "auto", maxHeight: "62vh", overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    {/* Stays put while the rows scroll — nine columns are hard
                        to read once the header is gone. */}
                    <thead style={{ background: "#fafbff", position: "sticky", top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ ...th, width: 56 }}>#</th>
                        <th style={th}>{multiType ? "Type" : "Linked"}</th>
                        <th style={th}>Sent to</th>
                        <th style={th}>Plan</th>
                        <th style={{ ...th, textAlign: "right" }}>Amount / due</th>
                        <th style={{ ...th, textAlign: "right" }}>Grace / Reminder</th>
                        <th style={th}>Channel</th>
                        <th style={th}>Status</th>
                        <th style={th}>Sent by</th>
                        <th style={th}>Date</th>
                        <th style={{ ...th, textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => {
                        /* effective_status, not status: a send past its
                           valid_until reads as expired without anyone having
                           had to mark it. */
                        const state = row.effective_status || row.status;
                        /* A proposal is an offer; everything else on these
                           screens — a quotation, or the renewal raised for the
                           next term — is a bill. The money affordances follow
                           that split rather than the word "quotation", so a
                           renewal is not left without a pay link. */
                        const isProposal = row.doc_type === "proposal";
                        const carriesMoney = !isProposal;
                        const pay = PAYMENT_STYLE[row.payment_status];
                        const linkDead = row.payment_link_expires_at
                          ? dayjs.utc(row.payment_link_expires_at).isBefore(dayjs.utc())
                          : false;
                        /* A dead link is not worth opening or copying — the
                           client would land on the gateway's own error. */
                        const payable = carriesMoney && row.payment_link
                          && row.payment_status !== "paid" && !linkDead;
                        /* Reachable after the fact too: a rep who dismissed the
                           question on acceptance, or who is told about the
                           transfer days later, needs the same form. */
                        const recordable = carriesMoney && state === "accepted"
                          && row.payment_status !== "paid";
                        /* The quotation this proposal produced. The API names it
                           outright; the same-page search is the fallback for
                           rows served before that field existed, and only ever
                           finds anything on a screen holding both kinds. */
                        const childId = row.child_send_id
                          ?? (isProposal && state === "accepted"
                            ? rows.find((r) => r.parent_send_id === row.id)?.id
                            : null);
                        /* Where it actually went: a WhatsApp send has a number,
                           not an address. Falls back to the other one so a row
                           never shows a blank destination. */
                        const destination = row.channel === "whatsapp"
                          ? row.sent_to_phone || row.sent_to_email
                          : row.sent_to_email || row.sent_to_phone;
                        /* A downloaded document has not gone anywhere — the rep
                           has the PDF and still has to hand it over. The server
                           records the send as "sent" either way, so saying so
                           here contradicts the Channel column beside it and
                           leaves a rep believing the client was mailed. Once the
                           client actually responds the real status takes over. */
                        const shown = state === "sent" && row.channel === "download"
                          ? "downloaded"
                          : state;
                        const st = STATUS_STYLE[shown] || STATUS_STYLE.expired;
                        return (
                          <tr
                            key={row.id}
                            /* The row is the document: clicking it opens the
                               thing you were reading about. */
                            onClick={() => reopen(row, row.doc_type)}
                            style={{
                              borderTop: "1px solid #f1f5f9",
                              cursor: opening ? "wait" : "pointer",
                              background: highlighted === row.id
                                ? "#fef9c3"
                                : hovered === row.id ? "#fafbff" : undefined,
                              transition: "background .3s ease",
                            }}
                            onMouseEnter={() => setHovered(row.id)}
                            onMouseLeave={() => setHovered(null)}
                            id={`send-row-${row.id}`}
                          >
                            <td style={{ ...td, color: "#9ca3af" }}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                            <td style={td}>
                              {/* On a single-type screen every badge would read
                                  the same, so the column carries only the links.
                                  The quotation screen keeps it — there it tells
                                  a renewal apart from a quotation a rep sent. */}
                              {multiType && (
                                <span style={badge(TYPE_STYLE[row.doc_type] || TYPE_STYLE.proposal)}>
                                  {capitalise(row.doc_type)}
                                </span>
                              )}
                              {row.parent_send_id && (
                                <div
                                  onClick={(e) => { e.stopPropagation(); openLinked(row.parent_send_id, "proposal"); }}
                                  style={{ fontSize: 11, color: "#5b48e8", marginTop: multiType ? 4 : 0, cursor: "pointer", fontWeight: 600 }}
                                >
                                  from proposal #{row.parent_send_id}
                                </div>
                              )}
                              {childId && (
                                <div
                                  onClick={(e) => { e.stopPropagation(); openLinked(childId, "quotation"); }}
                                  style={{ fontSize: 11, color: "#0d9488", marginTop: multiType ? 4 : 0, cursor: "pointer", fontWeight: 600 }}
                                >
                                  quotation sent →
                                </div>
                              )}
                              {/* Nothing to link to yet — said plainly rather
                                  than leaving the cell blank. */}
                              {!multiType && !row.parent_send_id && !childId && (
                                <span style={{ color: "#d1d5db" }}>—</span>
                              )}
                            </td>
                            <td style={{ ...td, wordBreak: "break-word", minWidth: 190 }}>
                              <div style={{ fontWeight: 700, color: "#111827" }}>{row.client_name || "—"}</div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                {joinDot([row.recipient_name, destination])}
                              </div>
                            </td>
                            <td style={{ ...td, whiteSpace: "nowrap" }}>
                              <div>{row.plan_name || "—"}</div>
                              <div style={{ fontSize: 11.5, color: "#9ca3af" }}>
                                {joinDot([
                                  row.marks ? `${Number(row.marks).toLocaleString("en-IN")} marks` : "",
                                  capitaliseOrBlank(row.frequency),
                                ])}
                              </div>
                            </td>
                            {/* Quoted, then what is actually settled against it.
                                A part payment is normal here, so the balance is
                                a figure to chase rather than an exception — and
                                the row is where someone is already looking when
                                they wonder. Both come from the API; nothing is
                                differenced on screen. */}
                            <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                              {inr(row.amount)}
                              {Number(row.amount_received) > 0 && (
                                <div style={{ fontSize: 11.5, color: "#16a34a", marginTop: 2 }}>
                                  {inr(row.amount_received)} received
                                </div>
                              )}
                              {Number(row.amount_due) > 0 && (
                                <div style={{ fontSize: 11.5, color: "#b45309", fontWeight: 700, marginTop: 2 }}>
                                  {inr(row.amount_due)} to collect
                                </div>
                              )}
                            </td>
                            <Tooltip title="Days active past expiry / days before expiry the reminders start">
                              <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>
                                {row.grace_period_days == null && row.reminder_before_days == null
                                  ? "—"
                                  : `${days(row.grace_period_days)} / ${days(row.reminder_before_days)}`}
                              </td>
                            </Tooltip>
                            <td style={{ ...td, color: "#6b7280" }}>{capitalise(row.channel)}</td>
                            <td style={td}>
                              {/* The status is the action: picking a new one is
                                  how a rep records what the client did. */}
                              <span style={{
                                background: st.bg, color: st.color,
                                border: `1px solid ${st.border}`, borderRadius: 20,
                                padding: "2px 11px", fontSize: 11.5, fontWeight: 700,
                                whiteSpace: "nowrap",
                              }}>
                                {capitalise(shown)}
                              </span>
                              {state === "expired" && row.valid_until && (
                                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                                  lapsed {dayjs(row.valid_until).format("DD MMM YYYY")}
                                </div>
                              )}
                              {/* Only a quotation is paid for; a proposal has no
                                  money attached to it. */}
                              {carriesMoney && pay && (
                                <div style={{ marginTop: 5 }}>
                                  <span style={badge(pay)}>{pay.label}</span>
                                </div>
                              )}
                              {/* When the payment link itself stops working,
                                  which is not the same as the quotation
                                  lapsing — a rep chasing a payment needs to
                                  know the link they are chasing with is still
                                  alive. Older sends have no expiry recorded,
                                  and say nothing rather than guessing. */}
                              {carriesMoney && row.payment_link_expires_at
                                && row.payment_status !== "paid" && (
                                <div style={{
                                  fontSize: 11, marginTop: 3,
                                  color: linkDead ? "#9ca3af" : "#b45309",
                                }}>
                                  {linkDead ? "Link expired " : "Link expires "}
                                  {fmtLinkExpiry(row.payment_link_expires_at)}
                                </div>
                              )}
                            </td>
                            <td style={{ ...td, whiteSpace: "nowrap" }}>{row.sent_by_name || "—"}</td>
                            <td style={{ ...td, color: "#6b7280", whiteSpace: "nowrap" }}>{fmtDateTime(row.created_at)}</td>
                            <td
                              style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {/* Recording the outcome is the point of the log, so
                                  it is one click — and only offered while the
                                  outcome is still open. */}
                              <div style={{ display: "inline-flex", gap: 6 }}>
                                {payable && (
                                  <>
                                    <Tooltip title="Open the payment page">
                                      <button
                                        onClick={() => window.open(row.payment_link, "_blank", "noopener")}
                                        style={actionBtn("#b45309", "#fffbeb", "#fde68a")}
                                      >
                                        <LinkOutlined />
                                      </button>
                                    </Tooltip>
                                    <Tooltip title="Copy the payment link to send yourself">
                                      <button
                                        onClick={() => copyLink(row.payment_link)}
                                        style={actionBtn("#b45309", "#fffbeb", "#fde68a")}
                                      >
                                        <CopyOutlined />
                                      </button>
                                    </Tooltip>
                                  </>
                                )}
                                {recordable && (
                                  <Tooltip title="Record a payment received offline">
                                    <button
                                      onClick={() => setPayingFor(row)}
                                      style={actionBtn("#16a34a", "#f0fdf4", "#bbf7d0")}
                                    >
                                      <WalletOutlined />
                                    </button>
                                  </Tooltip>
                                )}
                                <Tooltip title="Open it again with the same inputs">
                                  <button
                                    onClick={() => reopen(row, row.doc_type)}
                                    disabled={Boolean(opening)}
                                    style={actionBtn()}
                                  >
                                    {opening === `${row.id}:${row.doc_type}` ? <LoadingOutlined /> : <EyeOutlined />}
                                  </button>
                                </Tooltip>
                                <Tooltip title={`Send the same client a ${OTHER[row.doc_type]}`}>
                                  <button
                                    onClick={() => reopen(row, OTHER[row.doc_type])}
                                    disabled={Boolean(opening)}
                                    style={actionBtn("#0d9488", "#f0fdfa", "#99f6e4")}
                                  >
                                    {opening === `${row.id}:${OTHER[row.doc_type]}` ? <LoadingOutlined /> : <SwapOutlined />}
                                  </button>
                                </Tooltip>
                                {!CLOSED.has(state) && (
                                  <>
                                    <Tooltip title="Mark accepted">
                                      <button
                                        onClick={() => accept(row)}
                                        disabled={updateStatus.isPending}
                                        style={actionBtn("#16a34a", "#f0fdf4", "#bbf7d0")}
                                      >
                                        <CheckOutlined />
                                      </button>
                                    </Tooltip>
                                    <Tooltip title="Mark rejected">
                                      <button
                                        onClick={() => updateStatus.mutate({ id: row.id, status: "rejected" })}
                                        disabled={updateStatus.isPending}
                                        style={actionBtn("#dc2626", "#fef2f2", "#fecaca")}
                                      >
                                        <CloseOutlined />
                                      </button>
                                    </Tooltip>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{ padding: "14px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", background: "#fafbff" }}>
                  {/* Said plainly, so a filtered view never leaves you wondering
                      whether there is more behind it. */}
                  <span style={{ fontSize: 12.5, color: "#6b7280" }}>
                    Showing <b>{(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + rows.length}</b>
                    {total ? <> of <b>{total}</b></> : null}
                  </span>
                  {pageCount > 1 && (
                    <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)} color="primary" />
                  )}
                </div>
              </>
            )}
          </div>

          {payingFor && (
            <OfflinePaymentModal
              row={payingFor}
              open
              onAccepted={() => markAccepted(payingFor)}
              onClose={() => setPayingFor(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProposalLogs;
